import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../api/types';
import { Roles, type RoleName } from '../auth/roles';

interface AuthState {
  token: string | null;
  user: User | null;
  /**
   * When the SP starts impersonating a user, their own (SP) session is stashed
   * here so it can be restored via endImpersonation. Persisted to localStorage
   * so the banner + "Return" button survive page reloads. Null when not
   * impersonating.
   */
  originalSession: { token: string; user: User } | null;
  setSession: (token: string, user: User) => void;
  logout: () => void;
  /**
   * Swap the active session to a target user's, stashing the current (SP)
   * session so endImpersonation can restore it. If already impersonating, the
   * ORIGINAL stash is preserved (you don't end up stuck inside a chain).
   */
  startImpersonation: (token: string, user: User) => void;
  /** Restore the stashed SP session and clear the impersonation state. No-op if not impersonating. */
  endImpersonation: () => void;
  isImpersonating: () => boolean;
  /** True if the user holds at least one of the listed roles (any-of, case-insensitive). */
  hasRole: (...names: RoleName[]) => boolean;
  /**
   * True if the user's effective rights include the given key. Effective rights
   * are pre-expanded by the server (role-tier ceiling intersected with the user's
   * group memberships), so this is a plain set-membership check — no wildcard
   * matching client-side.
   */
  hasRight: (key: string) => boolean;
  isAdmin: () => boolean;
  isServiceProvider: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      originalSession: null,
      setSession: (token, user) => set({ token, user }),
      logout: () => set({ token: null, user: null, originalSession: null }),
      startImpersonation: (token, user) => {
        const cur = get();
        // Preserve the first stash — if SP impersonates A then (without ending)
        // impersonates B, we keep the SP, not A, as originalSession.
        const original = cur.originalSession
          ?? (cur.token && cur.user ? { token: cur.token, user: cur.user } : null);
        set({ token, user, originalSession: original });
      },
      endImpersonation: () => {
        const orig = get().originalSession;
        if (orig) set({ token: orig.token, user: orig.user, originalSession: null });
      },
      isImpersonating: () => !!get().originalSession,
      hasRole: (...names) => {
        const held = (get().user?.roles ?? []).map((r) => r.toLowerCase());
        return names.some((n) => held.includes(n.toLowerCase()));
      },
      hasRight: (key) => {
        const k = key.toLowerCase();
        return (get().user?.effectiveRights ?? []).some((r) => r.toLowerCase() === k);
      },
      isAdmin: () => get().hasRole(Roles.Admin),
      isServiceProvider: () => get().hasRole(Roles.ServiceProvider),
    }),
    { name: 'template-net-auth' },
  ),
);
